import React, { useState } from 'react';
import { PublicLayout } from '@/components/Layout/PublicLayout';
import { Search, ChevronDown, HelpCircle, MessageSquare } from 'lucide-react';

export const FAQPage: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const faqs = [
    {
      id: 1,
      question: 'How do I place an order?',
      answer: 'Browse the products in the App, choose the items and quantities you want, add them to your cart, and proceed to checkout. Confirm your details and complete the available payment process.'
    },
    {
      id: 2,
      question: 'How can I track my order?',
      answer: 'You can check your order status through the App. Your order may show statuses such as Order Received, Confirmed, Preparing, Out for Delivery, Delivered or Cancelled.'
    },
    {
      id: 3,
      question: 'Can I cancel my order?',
      answer: 'You may request cancellation before your order has been processed or dispatched. Once an order has been prepared or dispatched, cancellation may no longer be possible.'
    },
    {
      id: 4,
      question: 'What if my order is damaged, incorrect or I am not happy with it?',
      answer: 'Please report the problem as soon as possible through the App or by contacting Yebo Eggs. We may request photographs or other information and will assess the issue for a replacement, refund or other appropriate solution.'
    },
    {
      id: 5,
      question: 'How do I get help with my order?',
      answer: 'Contact Yebo Eggs using the contact details provided in the App. Please have your order number available so that we can assist you more quickly.'
    }
  ];

  const filteredFaqs = faqs.filter(faq =>
    faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
    faq.answer.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <PublicLayout
      title="Frequently Asked Questions (FAQ)"
      subtitle="The five most common questions customers may have when using the Yebo Eggs App."
    >
      {/* Search Input */}
      <div className="relative mb-6">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search questions..."
          className="w-full h-12 pl-11 pr-4 rounded-xl border border-slate-200 bg-slate-50/50 text-slate-900 placeholder:text-slate-400 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
        />
      </div>

      {/* FAQ Accordion List */}
      {filteredFaqs.length === 0 ? (
        <div className="text-center py-12 space-y-3">
          <HelpCircle className="w-12 h-12 text-slate-300 mx-auto" />
          <h3 className="text-base font-bold text-slate-700">No questions found</h3>
          <p className="text-sm text-slate-500">Try searching with a different term or keyword.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredFaqs.map((faq, index) => {
            const isOpen = openIndex === index;
            return (
              <div
                key={faq.id}
                className={`border rounded-xl transition-all overflow-hidden ${
                  isOpen ? 'border-primary/30 shadow-sm bg-primary/[0.01]' : 'border-slate-200 hover:border-slate-300 bg-white'
                }`}
              >
                <button
                  onClick={() => setOpenIndex(isOpen ? null : index)}
                  className="w-full flex items-center justify-between gap-4 p-5 text-left cursor-pointer focus:outline-none"
                >
                  <span className="font-bold text-slate-900 text-base flex items-start gap-3">
                    <span className="shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary text-xs flex items-center justify-center font-extrabold mt-0.5">
                      {faq.id}
                    </span>
                    {faq.question}
                  </span>
                  <ChevronDown className={`w-5 h-5 shrink-0 text-slate-400 transition-transform duration-200 ${isOpen ? 'rotate-180 text-primary' : ''}`} />
                </button>

                {isOpen && (
                  <div className="px-5 pb-5 pt-1 pl-14 border-t border-slate-100 text-sm text-slate-600 leading-relaxed">
                    {faq.answer}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Support Callout */}
      <div className="mt-10 p-6 rounded-xl bg-slate-50 border border-slate-200 flex flex-col sm:flex-row items-center justify-between gap-4">
        <div className="flex items-center gap-3 text-center sm:text-left">
          <div className="w-10 h-10 rounded-full bg-primary/10 text-primary flex items-center justify-center shrink-0">
            <MessageSquare className="w-5 h-5" />
          </div>
          <div>
            <h4 className="font-bold text-slate-900 text-sm">Need further assistance?</h4>
            <p className="text-xs text-slate-500">Contact Yebo Eggs using the contact details provided in the App.</p>
          </div>
        </div>
      </div>
    </PublicLayout>
  );
};
